import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { FormsModule } from '@angular/forms';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs';
import { RiskAlertTaskDetail } from '../../../../../models/task.model';
import { shortenAddress } from '../../../../../utils/shortenAddress.utils';
import { RiskMonitoringService } from '@olv/ui-shared/services/risk-monitoring.service';
import {
  RiskAssessmentProviderEnum,
  RiskLevelEnum,
  TaskResolutionEnum,
  TaskTypeEnum,
} from '@olv/ui-shared/enums/task.enums';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { ResolveTaskDto } from '@olv/ui-shared/models/task.model';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { SystemTransactionService } from '@olv/ui-shared/services/system-transaction.service';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { ButtonComponent, ButtonState, toButtonState } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-risk-alert-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    MatTooltipModule,
    StateBadgeComponent,
    HasPermissionDirective,
    ButtonComponent,
  ],
  templateUrl: './risk-alert-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./risk-alert-detail.component.scss'],
})
export class RiskAlertDetailComponent {
  @Input() detail?: RiskAlertTaskDetail;
  @Input() initiatedBy?: string;
  @Input() preparedDate?: Date;
  @Input() isHistory = false;
  @Input() resolution?: string;
  @Input() resolvedBy?: string;
  @Input() resolvedAt?: Date;
  @Input() comment!: string | undefined;
  @Input() taskId?: string;
  @Input() isAcknowledgeOnly = false;
  @Input() taskType?: TaskTypeEnum;

  @Output() walletDisabled = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  // Task resolution properties
  commentText = '';
  isProcessing = false;
  errorMessage = '';
  idTooltipText = 'Copy';

  private readonly toastr = inject(ToastService);
  private readonly riskService = inject(RiskMonitoringService);
  private readonly taskService = inject(TaskService);
  private readonly merchantTransactionService = inject(MerchantTransactionService);
  private readonly systemTransactionService = inject(SystemTransactionService);
  private readonly dialog = inject(MatDialog);

  protected readonly formatDateStandard = formatDateStandard;
  protected readonly shortenAddress = shortenAddress;
  protected readonly Scopes = Scopes;

  get transactionResource() {
    return this.taskType === TaskTypeEnum.SystemRisk ? Resources.SystemTransactions : Resources.MerchantTransactions;
  }

  get processingButtonState(): ButtonState {
    return toButtonState(this.isProcessing, false);
  }

  onClose(): void {
    this.closed.emit();
  }

  riskLevelToString(level: RiskLevelEnum): string {
    switch (level) {
      case RiskLevelEnum.NotAssessed:
        return 'Not Assessed';
      case RiskLevelEnum.Unknown:
        return 'Unknown';
      case RiskLevelEnum.Low:
        return 'Low';
      case RiskLevelEnum.Medium:
        return 'Medium';
      case RiskLevelEnum.High:
        return 'High';
      case RiskLevelEnum.Severe:
        return 'Severe';
      default:
        return 'Unknown';
    }
  }

  openReport(): void {
    if (this.detail?.reportUrl) {
      window.open(this.detail.reportUrl, '_blank');
    }
  }

  disableWallet(): void {
    if (this.isProcessing) return;

    const walletId = this.detail?.relatedWalletId || this.detail?.relatedWhitelistedWalletId;
    if (!walletId) return;

    const reason = `Risk alert: Address risk level elevated to ${this.riskLevelToString(
      this.detail!.currentRiskLevel,
    )}. Disabled for compliance review.`;

    this.isProcessing = true;

    this.riskService
      .disableWalletForRisk(walletId, reason)
      .pipe(finalize(() => (this.isProcessing = false)))
      .subscribe({
        next: () => {
          this.toastr.success('Wallet has been disabled');
          this.walletDisabled.emit(walletId);
        },
        error: (error) => {
          this.toastr.error('Failed to disable wallet');
        },
      });
  }

  canDisableWallet(): boolean {
    if (!this.detail || this.isHistory) return false;

    return (
      this.detail.currentRiskLevel >= RiskLevelEnum.High &&
      (this.detail.relatedWalletId || this.detail.relatedWhitelistedWalletId) !== undefined
    );
  }

  getRiskProviderName(provider: RiskAssessmentProviderEnum): string {
    switch (provider) {
      case RiskAssessmentProviderEnum.Chainalysis:
        return 'Chainalysis';
      default:
        return 'Unknown Provider';
    }
  }

  // Task resolution methods
  onApprove(): void {
    if (this.detail && this.detail.currentRiskLevel >= RiskLevelEnum.High) {
      this.openOTP();
    } else {
      this.resolveTask(TaskResolutionEnum.Approved);
    }
  }

  onReject(): void {
    this.resolveTask(TaskResolutionEnum.Rejected);
  }

  openOTP(): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Approve Risk Alert',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.resolveTask(TaskResolutionEnum.Approved, otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  getStatusClass(): string {
    if (!this.resolution) return 'status-pending';

    switch (this.resolution.toLowerCase()) {
      case 'approved':
        return 'status-completed';
      case 'rejected':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  }

  private resolveTask(
    resolution: TaskResolutionEnum,
    otp?: string,
    dialogRef?: MatDialogRef<OtpVerificationDialogComponent>,
  ): void {
    if (!this.taskId) return;

    this.isProcessing = true;
    this.errorMessage = '';

    if (dialogRef) {
      dialogRef.componentInstance.setLoadingState(true);
      dialogRef.disableClose = true;
    }

    const resolveDto: ResolveTaskDto = {
      resolution,
      comment: this.commentText?.trim() || undefined,
      attachmentIds: [],
      totp: otp,
    };

    this.taskService
      .resolveTask(this.taskId, resolveDto)
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          if (dialogRef) {
            dialogRef.disableClose = false;
            dialogRef.componentInstance.setLoadingState(false);
          }
        }),
      )
      .subscribe({
        next: () => {
          console.log(
            `Risk task ${resolution === TaskResolutionEnum.Approved ? 'approved' : 'rejected'} successfully.`,
          );
          if (dialogRef) dialogRef.close();
          this.refresh.emit();
          this.onClose();
        },
        error: (err) => {
          if (err.isOtpError && dialogRef) {
            dialogRef.componentInstance.otpForm.reset();
            dialogRef.componentInstance.focusInput(0);
            dialogRef.componentInstance.setErrorState(err.error?.message);
            return;
          }

          if (dialogRef) dialogRef.close();

          this.errorMessage =
            err.error?.message || `Failed to ${resolution === TaskResolutionEnum.Approved ? 'approve' : 'reject'} task`;
        },
      });
  }

  onAcknowledge(): void {
    if (!this.taskId || !this.detail?.relatedTransactionId) return;

    this.isProcessing = true;
    this.errorMessage = '';

    const request = { comment: this.commentText?.trim() || undefined };
    const transactionId = this.detail.relatedTransactionId;
    const taskId = this.taskId;

    const service$ =
      this.taskType === TaskTypeEnum.SystemRisk
        ? this.systemTransactionService.acknowledgeIncompleteRisk(transactionId, taskId, request)
        : this.merchantTransactionService.acknowledgeIncompleteRisk(transactionId, taskId, request);

    service$.pipe(finalize(() => (this.isProcessing = false))).subscribe({
      next: () => {
        this.refresh.emit();
        this.onClose();
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Failed to acknowledge incomplete risk assessment';
      },
    });
  }

  copyToClipboard(value: string, tooltip: MatTooltip) {
    navigator.clipboard.writeText(value).then(() => {
      this.idTooltipText = 'Copied';
      tooltip.show();

      setTimeout(() => {
        this.idTooltipText = 'Copy';
      }, 1500);
    });
  }
}
